import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Popover from "@radix-ui/react-popover";
import { ArrowRight, ArrowUpRight, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Text } from "@/components/Typography";
import { cn } from "@/utils/cn";
import { useToast } from "@/hooks/useToast";
import { useGettingStarted } from "@/hooks/useGettingStarted";
import { phaseOfTask, STEP_PAGES, type GettingStartedTaskKey } from "@/constants/gettingStarted";
import {
  clearReturnStep,
  hideStep,
  MAX_NUDGES,
  NUDGE_INTERVAL_MS,
  recordNudge,
  useReturnPrompt,
} from "../returnPromptStore";
import { ProgressRing } from "./GettingStartedWidget";
import { trackStep } from "../gettingStartedTracking";

/**
 * The Getting Started dock, pinned to the bottom of the sidebar.
 *
 * It has TWO faces, and which one shows is the whole idea:
 *
 *   PROGRESS CARD — the default. Ring, "Getting Started", the next step, and
 *   a chevron that opens the step list beside the sidebar. This is the ONLY
 *   way into the journey — there is no sidebar menu item for it. Picking a
 *   step (or the card itself) opens /getting-started on that step, which
 *   scrolls into view and pulses. On the Getting Started page the card shows
 *   as the active place and drops the chevron: the list is already on screen.
 *
 *   "FINISHED?" PROMPT — when the user is on a page where a step's work
 *   happens. Names the step, shows its position in the phase, and offers
 *   Not yet / Done. This closes the loop: every CTA routes the user away from
 *   the flow, and without this the only way to record that they did the thing
 *   is to remember to navigate back.
 *
 * THE NUDGE. If the flow is unfinished and the user is off exploring, the step
 * list opens itself after NUDGE_INTERVAL_MS — at most MAX_NUDGES times in a
 * session, with the full interval between each. It never fires on the Getting
 * Started page itself (they are already there), never over the "Finished?"
 * prompt (that is a more specific ask), and dismissing it starts the next
 * interval rather than cancelling the budget.
 */
export const GettingStartedDock = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  // True only while the popover is open because the nudge opened it, so it
  // can say why it appeared.
  const [nudged, setNudged] = useState(false);
  const { ret, hidden, nudges } = useReturnPrompt();
  const {
    currentPhase,
    isDone,
    firstUndoneKey,
    complete,
    completedCount,
    totalCount,
    overallPct,
    allComplete,
    isLoading,
  } = useGettingStarted();

  const onGettingStarted = location.pathname === "/getting-started";

  // Which step, if any, is this page asking about?
  const hosted = STEP_PAGES[location.pathname] ?? [];
  const contextKey = hosted.find((key) => !isDone(key));
  const returned = ret && !isDone(ret) ? ret : undefined;
  const askKey = returned ?? contextKey;
  const askLive = !!askKey && !hidden.includes(askKey) && !onGettingStarted && !isCollapsed;

  // Declared before any early return — React requires the hook order to be
  // identical on every render, and this component bails out below.
  const nudgeBlocked =
    isLoading || allComplete || onGettingStarted || askLive || open || nudges >= MAX_NUDGES;

  useEffect(() => {
    if (nudgeBlocked) return;
    const timer = window.setTimeout(() => {
      setOpen(true);
      setNudged(true);
      recordNudge();
    }, NUDGE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
    // Re-arming on `nudges` is what spaces them out: closing the popover sets
    // `open` false and the count has already gone up, so a fresh full-length
    // timer starts rather than the popover reappearing immediately.
  }, [nudgeBlocked, nudges]);

  if (isLoading || allComplete) return null;

  const nextKey = firstUndoneKey(currentPhase);
  const nextTask = currentPhase.tasks.find((t) => t.key === nextKey);
  // A step the user is working on but said "Not yet" to — the card names it
  // instead of the next step, so the work in hand is not forgotten.
  const wipTask =
    !askLive && askKey && !onGettingStarted
      ? phaseOfTask(askKey).tasks.find((t) => t.key === askKey)
      : undefined;

  const setPopover = (next: boolean) => {
    setOpen(next);
    if (!next) setNudged(false);
  };

  const go = (key?: GettingStartedTaskKey) => {
    setPopover(false);
    navigate(key ? `/getting-started?step=${encodeURIComponent(key)}` : "/getting-started");
  };

  /* ── "Finished?" prompt ─────────────────────────────────────────────── */

  if (askLive && askKey) {
    const phase = phaseOfTask(askKey);
    const idx = phase.tasks.findIndex((t) => t.key === askKey);
    const task = phase.tasks[idx];
    // Finishing a step the flow SENT you to returns you to the flow. Finishing
    // one you merely wandered into does not — yanking someone off the page
    // they chose to be on is not a reward for ticking a box.
    const cameFromFlow = returned === askKey;

    return (
      <div className="animate-in fade-in slide-in-from-bottom-1 rounded-[14px] border border-theme-border bg-theme-bg-surface p-3.5 shadow-sm duration-200">
        <button
          type="button"
          onClick={() => {
            clearReturnStep();
            go(askKey);
          }}
          title="Open in Getting Started"
          className="flex w-full items-center justify-between gap-2 text-left"
        >
          <Text
            as="span"
            className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-primary-purple"
          >
            Step {idx + 1} of {phase.tasks.length}
          </Text>
          <ArrowUpRight size={13} className="flex-shrink-0 text-theme-text-muted" />
        </button>

        <Text
          as="p"
          className="mt-1.5 text-[13.5px] font-semibold leading-[1.4] text-theme-text-heading"
        >
          {task.title}
        </Text>

        <div className="mt-3 flex gap-[3px]">
          {phase.tasks.map((t) => (
            <span
              key={t.key}
              className={cn(
                "h-1 flex-1 rounded-full",
                isDone(t.key)
                  ? "bg-success-500"
                  : t.key === askKey
                    ? "bg-primary-purple"
                    : "bg-theme-border-subtle",
              )}
            />
          ))}
        </div>

        <div className="mt-3.5 grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => hideStep(askKey)}
            className="inline-flex items-center justify-center rounded-[9px] border border-theme-border px-2.5 py-2 text-[12.5px] font-medium text-theme-text-secondary transition-colors hover:bg-theme-bg-hover"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={() => {
              trackStep(askKey, "step_completed", { method: "from_prompt" });
              complete(askKey);
              clearReturnStep();
              if (cameFromFlow) {
                go(askKey);
              } else {
                showToast({ type: "success", message: "Step done · progress saved" });
              }
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-[9px] bg-primary-purple px-2.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Check size={13} /> Done
          </button>
        </div>
      </div>
    );
  }

  /* ── Progress card ──────────────────────────────────────────────────── */

  // Collapsed rail is 64px — no room for a label, so the ring carries it.
  if (isCollapsed) {
    return (
      <button
        type="button"
        onClick={() => go()}
        title={`Getting Started — ${completedCount} of ${totalCount} steps done`}
        aria-label={`Getting Started, ${completedCount} of ${totalCount} steps done`}
        className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-theme-bg-hover"
      >
        <ProgressRing pct={overallPct} size={32} stroke={3} showLabel={false} />
      </button>
    );
  }

  const active = open || onGettingStarted;

  return (
    <Popover.Root open={open && !onGettingStarted} onOpenChange={setPopover}>
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl border p-2.5 transition-[background-color,border-color,box-shadow]",
          active
            ? "border-[color-mix(in_srgb,var(--color-primary-purple)_40%,transparent)] bg-theme-accent-subtle shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-primary-purple)_12%,transparent)]"
            : "border-theme-border bg-theme-bg-surface",
        )}
      >
        <button
          type="button"
          onClick={() => {
            go(nextKey);
          }}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <ProgressRing pct={overallPct} size={34} stroke={3} />
          <span className="min-w-0 flex-1">
            <Text
              as="span"
              className={cn(
                "block truncate text-[12.5px]",
                active ? "font-bold text-primary-purple" : "font-semibold text-theme-text-heading",
              )}
            >
              Getting Started
            </Text>
            <Text as="span" className="block truncate text-[11px] text-theme-text-secondary">
              {wipTask
                ? `In progress: ${wipTask.title}`
                : nextTask
                  ? `Next: ${nextTask.title}`
                  : `${completedCount} of ${totalCount} done`}
            </Text>
          </span>
        </button>

        {/* Horizontal, not vertical: the panel opens to the SIDE, so the
            chevron points the way it will travel. Hidden on the Getting
            Started page, where the step list is already on screen. */}
        {onGettingStarted ? (
          <Popover.Anchor />
        ) : (
          <Popover.Trigger asChild>
            <button
              type="button"
              title="Quick view"
              aria-label={open ? "Hide Getting Started steps" : "Show Getting Started steps"}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg text-theme-text-muted transition-colors hover:bg-theme-bg-hover"
            >
              {open ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
            </button>
          </Popover.Trigger>
        )}
      </div>

      <Popover.Portal>
        <>
          {/* A faint wash over the page, as in the design: it marks the
              popover as the thing in focus, and a click on it closes. */}
          <div
            aria-hidden
            onClick={() => setPopover(false)}
            className="fixed inset-0 z-40 animate-gs-fade bg-[var(--theme-bg-scrim)]"
          />
          <Popover.Content
            side="right"
            align="end"
            sideOffset={12}
            collisionPadding={16}
            className={cn(
              "z-50 w-[340px] max-w-[calc(100vw-280px)] origin-bottom-left overflow-hidden rounded-2xl",
              "border border-theme-border bg-theme-bg-surface shadow-xl focus:outline-none",
              "animate-gs-pop",
            )}
          >
            {/* Header — ring, progress line, phase, and a close affordance. */}
            <div className="flex items-start gap-3 border-b border-theme-border-subtle px-4 py-3.5">
              <ProgressRing pct={overallPct} size={34} stroke={3} />
              <div className="min-w-0 flex-1">
                <Text
                  as="p"
                  className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary-purple"
                >
                  Getting Started · {completedCount} of {totalCount} done
                </Text>
                <Text as="p" className="mt-0.5 text-[13.5px] font-semibold text-theme-text-heading">
                  Phase {currentPhase.id} · {currentPhase.title}
                </Text>
                {nudged && (
                  <Text as="p" className="mt-1 text-[11.5px] text-theme-text-secondary">
                    A few minutes free? Your next step is a quick one.
                  </Text>
                )}
              </div>
              <Popover.Close asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="-mr-1 flex-shrink-0 p-0.5 text-theme-text-muted transition-colors hover:text-theme-text-heading"
                >
                  <X size={15} />
                </button>
              </Popover.Close>
            </div>

            <div className="flex flex-col gap-0.5 p-1.5">
              {currentPhase.tasks.map((task, i) => {
                const done = isDone(task.key);
                const isNext = !done && task.key === nextKey;
                return (
                  <button
                    key={task.key}
                    type="button"
                    onClick={() => {
                      go(task.key);
                    }}
                    // Rows settle in one after another as the panel opens.
                    style={{ animationDelay: `${60 + i * 35}ms` }}
                    className={cn(
                      "group flex w-full animate-gs-row items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left transition-colors",
                      isNext ? "bg-theme-accent-subtle" : "hover:bg-theme-bg-hover",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-[1.5px]",
                        done
                          ? "border-transparent bg-success-500 text-white"
                          : isNext
                            ? "border-primary-purple"
                            : "border-theme-border-strong",
                      )}
                    >
                      {done && <Check size={9} strokeWidth={3} />}
                    </span>
                    <Text
                      as="span"
                      className={cn(
                        "min-w-0 flex-1 truncate text-[12.5px] leading-[1.4]",
                        done
                          ? "text-theme-text-muted line-through"
                          : isNext
                            ? "font-semibold text-theme-text-heading"
                            : "text-theme-text-secondary",
                      )}
                    >
                      {i + 1}. {task.title}
                    </Text>
                    {isNext && (
                      <span className="flex-shrink-0 rounded-full bg-theme-accent-subtle px-2 py-0.5 text-[10px] font-bold text-theme-badge-accent-text">
                        Next
                      </span>
                    )}
                    <ArrowRight
                      size={13}
                      className="flex-shrink-0 text-theme-text-muted opacity-60"
                    />
                  </button>
                );
              })}
            </div>

            <div className="px-4 pb-4 pt-1">
              <button
                type="button"
                onClick={() => {
                  go(nextKey);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-primary-purple px-3 py-3 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                Continue where I left off <ArrowRight size={14} />
              </button>
            </div>
          </Popover.Content>
        </>
      </Popover.Portal>
    </Popover.Root>
  );
};
